// Public store pages: browse and search assets, an asset's page (live 3D preview with its params, stats, licence and
// price, versions, install instructions) and a creator's assets. Data comes from the store service through
// store-client.ts (the sample store while the service isn't running).

import { getCatalogue } from "../catalogue.ts";
import { clear, h, link, toast } from "../dom.ts";
import { formatPrice } from "../money.ts";
import { setTitle } from "../router.ts";
import { itemOffer, NOT_AVAILABLE, selling, visibleItems } from "../selling.ts";
import { currentMe } from "../session.ts";
import { wornOnBody } from "../asset-preview.ts";
import { startAssetPreview } from "../preview-frame.ts";
import { ownedAssets, store, type StoreBackend } from "../store-client.ts";
import {
  ASSET_KIND_LABEL,
  assetFilterFromQuery,
  assetFilterToQuery,
  assetPath,
  changedParams,
  controlsFor,
  CONVENTION_LABEL,
  creatorStorePath,
  fmtBytes,
  fmtCount,
  installSnippets,
  isAssetId,
  LICENCE_LABEL,
  moneyItemId,
  snap,
  STATUS_LABEL,
  type AssetFilter,
  type AssetSummary,
  type Control,
  type Stats,
  type Value,
} from "../store.ts";

export const priceText = (coins: number): string => (coins > 0 ? formatPrice(coins) : "Free");
/** `priceText`, or "Not available yet" for a paid item while coins can't be sold (wave 9). */
export const offerText = (coins: number, isOn: boolean = selling()): string => (itemOffer(coins, isOn) === "unavailable" ? NOT_AVAILABLE : priceText(coins));

/** The browse filter as searched: without selling only free items, and no price sort (wave 9). */
export function sellingFilter(f: AssetFilter, isOn: boolean): AssetFilter {
  return isOn ? f : { ...f, price: "free", sort: f.sort === "price" ? "new" : f.sort };
}
const kindOne = (k: string): string => (ASSET_KIND_LABEL[k] ?? k).replace(/s$/, "");

export function sampleNote(b: StoreBackend): HTMLElement | null {
  return b.live ? null : h("p", { class: "note sample small" }, "Sample data (offline page work): the starter packages with made-up version histories. Changes last until you reload. Turn it off with ?sample-store=0.");
}

export function assetCard(a: AssetSummary, owned: Set<string> | null): HTMLElement {
  const mine = owned?.has(moneyItemId(a.id)) ?? false;
  return h(
    "li",
    { class: "card asset-card" },
    link(
      assetPath(a.id),
      { class: "card-link", "aria-label": `${a.title} by ${a.creator.name ?? a.creator.handle}, ${offerText(a.price)}${mine ? ", owned" : ""}` },
      h("div", { class: "card-art asset-art" }, a.previewUrl ? h("img", { src: a.previewUrl, alt: "", loading: "lazy" }) : h("span", { class: "card-art-title" }, a.title)),
      h(
        "div",
        { class: "card-meta" },
        h("span", { class: "card-title" }, a.title),
        h("span", { class: "card-sub" }, `${kindOne(a.kind)} · ${a.creator.name ?? a.creator.handle}`),
        h("span", { class: "asset-price" }, mine ? h("span", { class: "badge ok" }, "Owned") : offerText(a.price)),
      ),
    ),
  );
}

export function assetGrid(list: AssetSummary[], owned: Set<string> | null, empty = "Nothing here yet."): HTMLElement {
  if (!list.length) return h("p", { class: "dim" }, empty);
  return h("ul", { class: "grid asset-grid" }, list.map((a) => assetCard(a, owned)));
}

// ---- browse ------------------------------------------------------------------------------------------------------

export async function storeBrowsePage(_p: unknown, query: URLSearchParams): Promise<HTMLElement> {
  const backend = await store();
  const on = selling();
  // without selling (wave 9) paid items leave the lists, and ?price= / ?sort=price are ignored
  const all = visibleItems(await backend.search({}), on);
  const owned = await ownedAssets(currentMe()?.id ?? null);
  let f: AssetFilter = assetFilterFromQuery(query);
  if (!on) f = { ...f, price: null, sort: f.sort === "price" ? "new" : f.sort };
  setTitle(f.kind ? ASSET_KIND_LABEL[f.kind] ?? "Assets" : "Assets");

  const results = h("div", { class: "results" });
  const count = h("p", { class: "dim count", "aria-live": "polite" });
  const kindRow = h("div", { class: "filter-row tabs", role: "group", "aria-label": "Kind" });
  const conventionRow = h("div", { class: "filter-row", role: "group", "aria-label": "Rig" });
  const priceRow = h("div", { class: "filter-row", role: "group", "aria-label": on ? "Price" : "Sort" });
  const pill = (text: string, on: boolean, onclick: () => void) => h("button", { type: "button", class: `pill${on ? " on" : ""}`, "aria-pressed": on ? "true" : "false", onclick }, text);

  let seq = 0;
  let timer = 0;
  const update = (next: Partial<AssetFilter>, push = false, delay = 0) => {
    f = { ...f, ...next };
    const qs = assetFilterToQuery(f);
    if (location.search !== qs) history[push ? "pushState" : "replaceState"](null, "", `/store${qs}`);
    drawFilters();
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void drawResults(), delay);
  };

  const kinds = [...new Set(all.map((a) => a.kind))].sort((a, b) => Object.keys(ASSET_KIND_LABEL).indexOf(a) - Object.keys(ASSET_KIND_LABEL).indexOf(b));
  const conventions = [...new Set(all.flatMap((a) => a.conventions))].sort();
  const drawFilters = () => {
    clear(kindRow, pill("All", !f.kind, () => update({ kind: null }, true)), kinds.map((k) => pill(ASSET_KIND_LABEL[k] ?? k, f.kind === k, () => update({ kind: f.kind === k ? null : k }, true))));
    clear(
      conventionRow,
      conventions.length ? pill("Any rig", !f.convention, () => update({ convention: null }, true)) : null,
      conventions.map((c) => pill(CONVENTION_LABEL[c] ?? c, f.convention === c, () => update({ convention: f.convention === c ? null : c }, true))),
    );
    clear(
      priceRow,
      on ? pill("Any price", !f.price, () => update({ price: null }, true)) : null,
      on ? pill("Free", f.price === "free", () => update({ price: f.price === "free" ? null : "free" }, true)) : null,
      on ? pill("Paid", f.price === "paid", () => update({ price: f.price === "paid" ? null : "paid" }, true)) : null,
      h(
        "label",
        { class: "sort" },
        h("span", { class: "sr-only" }, "Sort"),
        h(
          "select",
          { onchange: (e: Event) => update({ sort: (e.target as HTMLSelectElement).value as AssetFilter["sort"] }) },
          [
            ["new", "Newest"],
            ["title", "A to Z"],
            ...(on ? [["price", "Price"]] : []),
          ].map(([v, t]) => h("option", { value: v, selected: (f.sort ?? "new") === v }, t)),
        ),
      ),
    );
  };
  const drawResults = async () => {
    const mine = ++seq;
    const list = await backend.search(sellingFilter(f, on)).then((l) => visibleItems(l, on)).catch((e) => {
      clear(results, h("p", { class: "error" }, e instanceof Error ? e.message : String(e)));
      return null;
    });
    if (!list || mine !== seq) return;
    count.textContent = `${list.length} ${list.length === 1 ? "asset" : "assets"}`;
    clear(results, assetGrid(list, owned, "No matches. Try fewer filters or another word."));
  };
  drawFilters();
  void drawResults();

  const search = h("input", {
    type: "search",
    id: "aq",
    placeholder: "Search characters, clothing, props, creators",
    value: f.q ?? "",
    autocomplete: "off",
    oninput: (e: Event) => update({ q: (e.target as HTMLInputElement).value }, false, 200),
  });

  return h(
    "div",
    { class: "browse store" },
    h("h1", null, "Assets"),
    h("p", { class: "lead" }, "Characters, clothing and props for three.js games, made by creators on Banger. They share one rig and one scale, so a shirt from one creator fits a character from another."),
    sampleNote(backend),
    h("form", { class: "search", role: "search", onsubmit: (e: Event) => e.preventDefault() }, h("label", { for: "aq", class: "sr-only" }, "Search assets"), search),
    kindRow,
    conventionRow,
    priceRow,
    count,
    results,
    h("p", { class: "dim small store-foot" }, "Made something? ", link("/dashboard/assets", null, "Publish your assets"), " from the creator dashboard."),
  );
}

// ---- asset page --------------------------------------------------------------------------------------------------

function control(c: Control, onChange: (v: Value) => void): HTMLElement {
  const id = `p-${c.key}`;
  if (c.kind === "range") {
    const out = h("span", { class: "value" }, String(c.value));
    return h(
      "div",
      { class: "slider" },
      h("div", { class: "slider-head" }, h("label", { for: id }, c.label), out),
      h("input", {
        type: "range",
        id,
        min: c.min,
        max: c.max,
        step: c.step,
        value: c.value,
        "aria-describedby": c.hint ? `${id}-h` : null,
        oninput: (e: Event) => {
          const v = snap(Number((e.target as HTMLInputElement).value), c.step);
          out.textContent = String(v);
          onChange(v);
        },
      }),
      c.hint ? h("span", { class: "dim small", id: `${id}-h` }, c.hint) : null,
    );
  }
  if (c.kind === "toggle") {
    return h("label", { class: "check param-check" }, h("input", { type: "checkbox", id, checked: c.value, onchange: (e: Event) => onChange((e.target as HTMLInputElement).checked) }), c.label);
  }
  if (c.kind === "colour") {
    return h("div", { class: "param-row" }, h("label", { for: id }, c.label), h("input", { type: "color", class: "swatch", id, value: c.value, oninput: (e: Event) => onChange((e.target as HTMLInputElement).value) }));
  }
  if (c.kind === "choice") {
    return h(
      "div",
      { class: "param-row" },
      h("label", { for: id }, c.label),
      h("select", { id, onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value) }, c.options.map((o) => h("option", { value: o, selected: o === c.value }, o))),
    );
  }
  return h("div", { class: "param-row" }, h("label", { for: id }, c.label), h("input", { id, value: c.value, onchange: (e: Event) => onChange((e.target as HTMLInputElement).value) }));
}

function codeBlock(title: string, code: string, hint?: HTMLElement | string): HTMLElement {
  const pre = h("pre", { class: "code" }, h("code", null, code));
  return h(
    "div",
    { class: "install-block" },
    h(
      "div",
      { class: "install-head" },
      h("h3", null, title),
      h(
        "button",
        {
          type: "button",
          class: "btn small ghost",
          onclick: async () => {
            try {
              await navigator.clipboard.writeText(pre.textContent ?? "");
              toast("Copied");
            } catch {
              toast("Couldn't copy. Select the text instead.");
            }
          },
        },
        "Copy",
      ),
    ),
    hint ? h("p", { class: "dim small" }, hint) : null,
    pre,
  );
}

export function statsList(s: Stats, budget?: { triangles?: number; drawCalls?: number }): HTMLElement {
  const item = (k: string, v: string | null, b?: number) => h("div", null, h("dt", null, k), h("dd", null, v ?? "—"), b !== undefined ? h("span", { class: "dim small" }, `budget ${fmtCount(b)}`) : null);
  return h(
    "dl",
    { class: "stats asset-stats" },
    item("Triangles", s.triangles !== undefined ? fmtCount(s.triangles) : null, budget?.triangles),
    item("Draw calls", s.drawCalls !== undefined ? String(s.drawCalls) : null, budget?.drawCalls),
    item("Download", s.bytes !== undefined ? fmtBytes(s.bytes) : null),
  );
}

function fmtDay(unix: number): string {
  return unix ? new Date(unix * 1000).toISOString().slice(0, 10) : "—";
}

/** ">=0.180.0 <0.190.0" → "r180 to r189" */
export function threeRange(peer?: string): string | null {
  if (!peer) return null;
  const lo = /^>=\s*0\.(\d+)/.exec(peer)?.[1];
  const hi = /<\s*0\.(\d+)\.0/.exec(peer)?.[1];
  return lo && hi ? `r${lo} to r${Number(hi) - 1}` : peer;
}

export async function assetPage(params: Record<string, string>): Promise<HTMLElement> {
  const id = `${params.creator}/${params.name}`;
  if (!isAssetId(id)) throw new Error("That isn't an asset address.");
  const backend = await store();
  const a = await backend.asset(id);
  if (!a) throw new Error("No asset by that name in the store.");
  setTitle(a.title);
  const me = currentMe();
  // the store's licence lookup (money's record) for this asset; money's inventory when the store can't say
  const lic = me && a.price > 0 ? await backend.licensed(a.id) : null;
  const owned = lic === null ? await ownedAssets(me?.id ?? null) : null;
  const isOwned = lic ?? owned?.has(moneyItemId(a.id)) ?? false;
  const on = selling();
  // a paid item while coins can't be sold (wave 9): its page stays, but says "Not available yet" (owners still install)
  const unavailable = itemOffer(a.price, on) === "unavailable" && !isOwned;
  const live = backend.openFiles(a);
  const runtime = await backend.runtime();
  const m = a.manifest;
  const worn = wornOnBody(m);

  let values: Record<string, Value> = {};
  for (const c of controlsFor(m, {}, worn)) values[c.key] = c.value;
  const measured: Stats = { ...a.stats };

  // ---- preview ----
  const stageNote = h("p", { class: "stage-note" }, "Loading the 3D preview…");
  const stage = h("div", { class: "stage asset-stage", role: "img", "aria-label": `3D preview of ${a.title}` }, stageNote);
  const statsBox = h("div", null, statsList(measured, m.budget));
  const statsNote = h("p", { class: "dim small" }, a.stats?.triangles !== undefined ? "From the store's checks at default params." : worn ? "Counted from the item's model at default params." : "Counted in your browser at the params you've set.");
  // paid assets: the store's public low-detail model, never their licence-gated files or code
  const model = !live && a.previewModel ? a.previewModel : null;
  const preview = live || model
    ? startAssetPreview(stage, {
    base: model ?? a.base,
    manifest: m,
    mode: model ? "preview" : "live",
    onNote: (t) => (stageNote.textContent = t),
    onStats: (s) => {
      if (a.stats?.triangles !== undefined) return; // the store's numbers win
      Object.assign(measured, s);
      clear(statsBox, statsList(measured, m.budget));
    },
  })
    : staticPreview(stage, stageNote, a.previewUrl);

  // ---- params + install snippets (the snippets follow the sliders) ----
  const snippets = h("div", { class: "install" });
  const drawInstall = () => {
    if (unavailable) return clear(snippets, h("p", { class: "dim" }, "This asset isn't available yet. Free assets install from here as usual."));
    const s = installSnippets(a.id, changedParams(m, values), { physics: !!m.physics, paid: a.price > 0 && !isOwned, runtime, site: location.origin });
    clear(
      snippets,
      codeBlock(
        "With your agent (MCP)",
        s.mcp,
        a.price > 0
          ? isOwned
            ? "You own a licence, so it installs free on any machine."
            : `Your agent shows you ${priceText(a.price)} and buys only after you say yes. One purchase covers every later version.`
          : "The store's install tool writes it to your project's assets/ folder, pins the version in assets.lock, and checks the store's signature and every file hash first.",
      ),
      h("details", { class: "mcp-config" }, h("summary", null, "Add the store to your agent"), h("pre", { class: "code" }, h("code", null, s.mcpConfig)), h("p", { class: "dim small" }, "Its tools: ", h("code", null, "search"), ", ", h("code", null, "get"), ", ", h("code", null, "preview"), ", ", h("code", null, "install"), ". Creators also get ", h("code", null, "publish"), " and ", h("code", null, "yank"), ".")),
      codeBlock("In a three.js game", s.three, "Assets get your game's own three.js. Each frame is yours: call the asset's behaviours from your loop."),
      codeBlock(
        "From a terminal (Threedom CLI)",
        s.cli,
        a.price > 0 && !isOwned
          ? h("span", null, `It shows you ${priceText(a.price)} and stops. Add `, h("code", { class: "nowrap" }, "--confirm"), " only once you've decided to buy.")
          : "The same checks as your agent: the store's signature and every file hash, then assets/ and assets.lock. The CLI's thrdm outdated lists newer versions.",
      ),
    );
  };
  const controls = controlsFor(m, values, worn);
  const paramsPanel = h("div", { class: "params" });
  const drawParams = () =>
    clear(
      paramsPanel,
      controls.length === 0
        ? h("p", { class: "dim small" }, "This asset has no settings.")
        : controlsFor(m, values, worn).map((c) =>
            control(c, (v) => {
              values = { ...values, [c.key]: v };
              if (!model) preview.show(values); // the preview model is fixed at the defaults
              drawInstall();
            }),
          ),
      controls.length
        ? h(
            "button",
            {
              type: "button",
              class: "btn small ghost",
              onclick: () => {
                values = {};
                for (const c of controlsFor(m, {}, worn)) values[c.key] = c.value;
                drawParams();
                if (!model) preview.show(values);
                drawInstall();
              },
            },
            "Reset",
          )
        : null,
      live ? null : h("p", { class: "dim small" }, model ? "The 3D view is a lower-detail preview at the default settings: the full model and these settings come with a licence. The install code below follows them." : "The picture shows the default settings. The install code below still follows them."),
      worn && (live || model) ? h("p", { class: "dim small" }, "Shown worn on the standard body. It fits any body on the humanoid rig: the wearer's height and shape come from their avatar.") : null,
    );
  drawParams();
  drawInstall();
  preview.show(values);

  const licence = LICENCE_LABEL[a.licence] ?? { name: a.licence, text: "" };
  const listed = a.versions.filter((v) => v.status === "listed" || v.status === "yanked");
  const three = threeRange(m.engines?.three?.peer?.three);

  return h(
    "article",
    { class: "asset-page" },
    sampleNote(backend),
    h(
      "div",
      { class: "asset-top" },
      h("div", { class: "stage-wrap" }, stage),
      h(
        "div",
        { class: "asset-side" },
        h("p", { class: "dim small crumbs" }, link("/store", null, "Assets"), " / ", link(`/store?kind=${encodeURIComponent(a.kind)}`, null, ASSET_KIND_LABEL[a.kind] ?? a.kind)),
        h("h1", null, a.title),
        h("p", { class: "dim" }, "by ", link(creatorStorePath(a.creator.handle), null, a.creator.name ?? a.creator.handle), a.creator.verified ? h("span", { class: "badge ok verified" }, "Verified") : null, ` · v${a.version}`),
        h(
          "div",
          { class: "buy-box" },
          on || !(a.price > 0) ? h("div", { class: "asset-price big" }, priceText(a.price)) : isOwned ? null : h("div", { class: "asset-price big" }, NOT_AVAILABLE),
          isOwned ? h("span", { class: "badge ok" }, "Owned") : null,
          h("p", { class: "small" }, h("strong", null, `${licence.name} licence. `), licence.text),
          on && a.price > 0 && !isOwned ? h("p", { class: "dim small" }, "You pay when you install it with the Threedom CLI or your agent, after you confirm. One purchase covers every later version.") : null,
          on && a.cosmetic ? h("p", { class: "dim small" }, "Players can also buy it to wear on their avatar. Wearing it and shipping it in a game are separate purchases.") : null,
        ),
        h("ul", { class: "chips" }, h("li", null, kindOne(a.kind)), a.conventions.map((c) => h("li", null, CONVENTION_LABEL[c] ?? c)), m.physics ? h("li", null, "Physics (Rapier)") : null, a.tags.slice(0, 5).map((t) => h("li", null, t))),
        h("section", { class: "panel params-panel" }, h("h2", null, "Try it"), paramsPanel),
      ),
    ),
    h(
      "div",
      { class: "asset-body" },
      h("section", { class: "section" }, h("h2", null, "Stats"), statsBox, statsNote),
      h("section", { class: "section" }, h("h2", null, "Install in your project"), snippets),
      h(
        "section",
        { class: "section" },
        h("h2", null, "Details"),
        h(
          "dl",
          { class: "facts" },
          fact("Works with", three ? `three.js ${three}` : "Any glTF viewer (baked model)"),
          fact("Scale", `${m.units ?? "metres"}, ${m.up ?? "+Y"} up`),
          m.conventions?.length ? fact("Follows", m.conventions.join(", ")) : null,
          m.wear?.slot ? fact("Worn on", m.wear.slot) : null,
          fact("Code may use", m.capabilities?.length ? m.capabilities.join(", ") : "Nothing beyond its own files"),
          m.behaviours?.length ? fact("Behaviours", m.behaviours.join(", ")) : null,
          fact("Deterministic", m.deterministic ? "Yes: the same params and seed always give the same result" : "No"),
          a.downloads !== undefined ? fact("Installs", fmtCount(a.downloads)) : null,
        ),
      ),
      h(
        "section",
        { class: "section" },
        h("h2", null, "Versions"),
        h(
          "div",
          { class: "panel table-wrap" },
          h(
            "table",
            null,
            h("thead", null, h("tr", null, h("th", null, "Version"), h("th", null, "Published"), h("th", null, "Status"))),
            h(
              "tbody",
              null,
              listed.map((v) =>
                h(
                  "tr",
                  null,
                  h("td", null, h("code", null, v.version)),
                  h("td", null, fmtDay(v.publishedAt)),
                  h("td", null, h("span", { class: `badge ${STATUS_LABEL[v.status].cls}` }, v.status === "listed" ? (v.signed ? "Signed" : "Listed") : STATUS_LABEL[v.status].text), v.yankReason ? h("span", { class: "dim small yank-reason" }, ` ${v.yankReason}`) : null),
                ),
              ),
            ),
          ),
          h("p", { class: "dim small" }, "Versions never change once published. A yanked version stays installable for games that pinned it, with a warning."),
        ),
      ),
    ),
  );
}

/** The store's picture in place of the 3D view (paid assets: their files need a licence). */
function staticPreview(stage: HTMLElement, note: HTMLElement, url: string | null): { show(p: Record<string, Value>): void } {
  stage.classList.add("still");
  if (url) stage.prepend(h("img", { src: url, alt: "", class: "stage-img" }));
  note.textContent = url ? "Picture at the default settings." : "No preview picture.";
  return { show() {} };
}

function fact(k: string, v: string): HTMLElement {
  return h("div", null, h("dt", null, k), h("dd", null, v));
}

// ---- creator's assets --------------------------------------------------------------------------------------------

export async function storeCreatorPage(params: Record<string, string>): Promise<HTMLElement> {
  const backend = await store();
  const c = await backend.creator(params.handle ?? "");
  if (!c) throw new Error("No creator by that name on Banger.");
  const name = c.name ?? c.handle;
  setTitle(`${name}: assets`);
  const owned = await ownedAssets(currentMe()?.id ?? null);
  const on = selling();
  const shown = visibleItems(c.assets, on);
  const paid = on ? c.assets.filter((a) => a.price > 0).length : 0;
  const games = c.creatorId ? await getCatalogue().then((cat) => cat.creators.some((x) => x.id === c.creatorId), () => false) : false;
  return h(
    "article",
    { class: "creator" },
    sampleNote(backend),
    h(
      "header",
      { class: "creator-head" },
      h("div", { class: "avatar-badge", "aria-hidden": "true" }, name.slice(0, 1).toUpperCase()),
      h(
        "div",
        null,
        h("h1", null, name),
        h("p", { class: "dim" }, `${shown.length} ${shown.length === 1 ? "asset" : "assets"}`, paid ? ` · ${paid} paid` : null, c.creatorId ? [" · ", link(`/c/${c.creatorId}`, null, games ? "Games" : "Creator page")] : null),
      ),
    ),
    h("section", { class: "section" }, h("h2", null, "Assets"), assetGrid(shown, owned, "No assets yet.")),
  );
}
